import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  fetchPlayersStatsForCompetitionActiveSeason,
  fetchHistoricalPlayerStats,
  fetchAvailableHistoricalSeasons,
} from '@/api/stats';
import { fetchUpcomingContests } from '@/api/dfs';
import { useAuth } from '@/contexts/AuthContext';
import { getAbbreviation } from '@/utils/teams';
import { formatPlayerDisplayName } from '@/utils/formatting';
import Spinner from '@/components/common/Spinner';
import { compareContestsCombinedFirst } from '@/utils/contestLobby';
import { userFacingError } from '@/utils/userFacingError';

// Internal DB competition ids (EPL=1, La Liga=4 - per project memory).
const EPL_COMPETITION_ID = 1;
const LALIGA_COMPETITION_ID = 4;

// League toggle above the showcase table. Combined is primary + default,
// EPL secondary, La Liga tertiary (owner order, 2026-07-27). Scoring is
// identical across comps, so the combined top-10 is a fair merge.
// Landing-showcase eligibility (owner 2026-07-28): a 2-game 26.5-FPPG
// sample (Yeremy Pino) is not a top-10 season - require a real body of work.
const MIN_GAMES_FOR_SHOWCASE = 10;
// Fetch deep enough that filtering still leaves a full 10.
const SHOWCASE_FETCH_LIMIT = 50;
// Early-season bridge (owner 2026-08-26): until the live season has a full
// table's worth of players at the games floor, show that comp's most recent
// archived season instead of an empty (or 1-3 row) table. The archive
// identifier is looked up from /stats/historical/seasons, so it rolls
// forward every August with no code change. Review 2026-08-26: switch on
// "10 qualifying rows", not "one qualifying player in the top 50", to avoid
// the GW10 cliff and most of the mixed-season combined state.
const MIN_QUALIFYING_ROWS_FOR_LIVE = 10;
// "EPL 2025/2026" -> "2025/26" for the header tag while the bridge is active.
const shortSeasonLabel = (identifier: string): string => {
  const m = identifier.match(/(\d{4})\/(\d{4})/);
  return m ? `${m[1]}/${m[2].slice(2)}` : identifier;
};
// Owner-curated list edits for the MARKETING table only (in-app stats and
// rankings are untouched). Excluded: Antony (Betis, 81254) and Azzedine
// Ounahi (Girona, 100701) - real numbers, wrong first impression on a
// billboard. Pinned: Bruno Guimaraes (Newcastle, 81188) always shows when
// he clears the games floor, seeded in his natural FPPG position.
const SHOWCASE_EXCLUDED_PLAYER_IDS = new Set([81254, 100701]);
const SHOWCASE_PINNED_PLAYER_IDS = new Set([81188]);

type ScopeKey = 'combined' | 'epl' | 'laliga';
const SCOPES: Array<{ key: ScopeKey; label: string; compIds: number[] }> = [
  { key: 'combined', label: 'EPL + La Liga', compIds: [EPL_COMPETITION_ID, LALIGA_COMPETITION_ID] },
  { key: 'epl', label: 'EPL', compIds: [EPL_COMPETITION_ID] },
  { key: 'laliga', label: 'La Liga', compIds: [LALIGA_COMPETITION_ID] },
];

// Loose shape of a row from /stats/.../active-season/players —
// covers both the raw stats entry shape and the partially-transformed shape.
interface PlayerStatsEntry {
  id?: number;
  player_id?: number;
  player_name?: string | null;
  name?: string | null;
  nickname?: string | null;
  current_team_name?: string | null;
  primary_position?: string | null;
  fantasy_position?: string | null;
  fantasy_position_in_league?: string | null;
  fppg?: number | null;
  player_games_appeared?: number | null;
  goals?: number | null;
  shots?: number | null;
  shots_on_target?: number | null;
  sca?: number | null;
  gca?: number | null;
  prog_passes?: number | null;
  prog_carries?: number | null;
  dispossessed?: number | null;
}

const RankBadge: React.FC<{ rank: number }> = ({ rank }) => (
  <span className="inline-flex items-center justify-center rounded-md bg-accent-yellow text-primary font-heading font-bold tracking-wide shadow-[0_2px_8px_rgba(255,196,44,0.25)] min-w-[28px] sm:min-w-[40px] h-[24px] sm:h-[30px] px-2 sm:px-3 text-caption sm:text-sm">
    {rank}
  </span>
);

const TeamPosPill: React.FC<{ team: string | null | undefined; position: string | null | undefined }> = ({
  team,
  position,
}) => {
  if (!team && !position) return null;
  return (
    <span className="inline-flex flex-none items-center px-1.5 sm:px-2 py-0 sm:py-0.5 rounded bg-gray-200/10 text-gray-300 font-mono font-bold text-micro sm:text-badge tracking-wider whitespace-nowrap">
      {team || '—'}
      {team && position && <span className="text-gray-500"> · </span>}
      {position && <span className="text-accent-yellow">{position}</span>}
    </span>
  );
};

const StatCell: React.FC<{ value: number }> = ({ value }) => (
  <td
    className={[
      'px-2 py-3 sm:px-3 sm:py-4 text-center whitespace-nowrap font-mono font-medium text-sm',
      value === 0 ? 'text-gray-600' : 'text-gray-200',
    ].join(' ')}
  >
    {value}
  </td>
);

const TopPlayersSection: React.FC = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [scope, setScope] = useState<ScopeKey>('combined');
  // Per scope: the rendered rows plus the archive season label when any comp
  // in the scope is served from the bridge (null = fully live).
  const [playersByScope, setPlayersByScope] = useState<
    Partial<Record<ScopeKey, { rows: PlayerStatsEntry[]; priorLabel: string | null }>>
  >({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nextContestId, setNextContestId] = useState<number | null>(null);

  // Fetch the selected scope's top 10 by FPPG. Each comp's own top 10 is
  // enough for the combined view: any combined top-10 player is by
  // definition inside his league's top 10. Results cache per scope so
  // toggling pills never refetches.
  useEffect(() => {
    if (playersByScope[scope]) {
      // Serving cache: clear any in-flight state left by a scope the user
      // clicked through (its fetch cancels without ever resetting these -
      // review catch 2026-07-28: rapid Combined -> EPL -> Combined left the
      // cached table behind a permanent spinner / stale error).
      setIsLoading(false);
      setError(null);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    const compIds = SCOPES.find((sc) => sc.key === scope)?.compIds ?? [EPL_COMPETITION_ID];
    const showcaseFilters = { sort_by: 'fppg' as const, order: 'desc' as const, skip: 0, limit: SHOWCASE_FETCH_LIMIT };
    const meetsFloor = (r: PlayerStatsEntry) =>
      (r.player_games_appeared ?? 0) >= MIN_GAMES_FOR_SHOWCASE;
    // Latest archived identifier per comp (same prefix per comp, so string
    // order is chronological). Resolved lazily - only when a comp needs it.
    let seasonsPromise: Promise<Map<number, string>> | null = null;
    const latestArchivedIdentifier = (compId: number): Promise<string | null> => {
      if (!seasonsPromise) {
        seasonsPromise = fetchAvailableHistoricalSeasons(token).then((seasons) => {
          const latest = new Map<number, string>();
          for (const sn of seasons) {
            const cur = latest.get(sn.competition_id);
            if (!cur || sn.season_identifier > cur) latest.set(sn.competition_id, sn.season_identifier);
          }
          return latest;
        });
      }
      return seasonsPromise.then((m) => m.get(compId) ?? null);
    };
    Promise.all(
      compIds.map(async (compId): Promise<{ rows: PlayerStatsEntry[]; prior: string | null }> => {
        const live = (await fetchPlayersStatsForCompetitionActiveSeason(
          compId, showcaseFilters, token
        )) as PlayerStatsEntry[];
        if (live.filter(meetsFloor).length >= MIN_QUALIFYING_ROWS_FOR_LIVE) {
          return { rows: live, prior: null };
        }
        // Any failure on the bridge path degrades to the live list (the
        // pre-fix behaviour) rather than surfacing an archive error on the
        // marketing table.
        try {
          const prior = await latestArchivedIdentifier(compId);
          if (!prior) return { rows: live, prior: null };
          const archived = (await fetchHistoricalPlayerStats(
            compId, prior, showcaseFilters, token
          )) as PlayerStatsEntry[];
          return { rows: archived, prior };
        } catch {
          return { rows: live, prior: null };
        }
      })
    )
      .then((results) => {
        if (cancelled) return;
        const priorIdentifiers = results.map((r) => r.prior).filter((p): p is string => !!p);
        const priorLabel = priorIdentifiers.length
          ? shortSeasonLabel(priorIdentifiers[0])
          : null;
        const rows = (results.flatMap((r) => r.rows).filter(Boolean) as PlayerStatsEntry[]).filter(
          (r) => meetsFloor(r) && !SHOWCASE_EXCLUDED_PLAYER_IDS.has(Number(r.id ?? r.player_id))
        );
        rows.sort((a, b) => (b.fppg ?? 0) - (a.fppg ?? 0));
        // A January cross-league mover can surface from both comps - keep
        // his better row only.
        const seen = new Set<number>();
        const deduped: PlayerStatsEntry[] = [];
        for (const r of rows) {
          const pid = Number(r.id ?? r.player_id);
          if (Number.isFinite(pid)) {
            if (seen.has(pid)) continue;
            seen.add(pid);
          }
          deduped.push(r);
        }
        // Pinned players claim their seats first; remaining seats fill by
        // FPPG. Final order is pure FPPG either way, so a pinned player sits
        // wherever his number puts him - never artificially on top.
        const pinned = deduped.filter((r) =>
          SHOWCASE_PINNED_PLAYER_IDS.has(Number(r.id ?? r.player_id)));
        const rest = deduped.filter((r) =>
          !SHOWCASE_PINNED_PLAYER_IDS.has(Number(r.id ?? r.player_id)));
        const top = [...pinned, ...rest.slice(0, Math.max(0, 10 - pinned.length))];
        top.sort((a, b) => (b.fppg ?? 0) - (a.fppg ?? 0));
        setPlayersByScope((prev) => ({ ...prev, [scope]: { rows: top, priorLabel } }));
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(userFacingError(err, 'Could not load top players.'));
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, scope, playersByScope]);

  const players = playersByScope[scope]?.rows ?? [];
  const priorLabel = playersByScope[scope]?.priorLabel ?? null;
  const scopeLabel = SCOPES.find((sc) => sc.key === scope)?.label ?? 'EPL';

  // Fetch next upcoming contest for the Build-a-Lineup CTA — same pattern as the carousel
  useEffect(() => {
    fetchUpcomingContests(token, 14)
      .then((contests) => {
        const weekend = contests
          .filter((contest) => contest.is_weekend_contest)
          .sort(compareContestsCombinedFirst)[0];
        if (weekend) setNextContestId(weekend.id);
        else if (contests.length > 0) setNextContestId(contests[0].id);
      })
      .catch(() => {
        /* silently fall back to /contests */
      });
  }, [token]);

  const statColumns: Array<{ label: string; key: keyof PlayerStatsEntry }> = [
    { label: 'GLs', key: 'goals' },
    { label: 'SoT', key: 'shots_on_target' },
    { label: 'Shots', key: 'shots' },
    { label: 'GC', key: 'gca' },
    { label: 'SC', key: 'sca' },
    { label: 'IP', key: 'prog_passes' },
    { label: 'IC', key: 'prog_carries' },
    { label: 'DIS', key: 'dispossessed' },
  ];

  return (
    <div className="py-12 px-4">
      <div className="max-w-5xl mx-auto">
        <h2 className="text-2xl md:text-3xl font-heading font-bold text-center text-white mb-6 px-2 leading-tight">
          The Players Who Dominate the Match <span className="text-accent-yellow">Dominate Fantasy</span>
        </h2>

        <div className="flex items-center justify-center gap-2 mb-4" role="tablist" aria-label="League">
          {SCOPES.map((sc) => {
            const active = sc.key === scope;
            return (
              <button
                key={sc.key}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setScope(sc.key)}
                className={`px-4 py-1.5 rounded-full text-xs sm:text-sm font-bold whitespace-nowrap transition-colors border ${
                  active
                    ? 'bg-accent-yellow text-primary border-accent-yellow'
                    : 'bg-primary-dark text-gray-300 hover:text-white border-gray-700'
                }`}
              >
                {sc.label}
              </button>
            );
          })}
        </div>

        <div className="w-full mb-6">
          <div className="relative overflow-hidden rounded-lg border border-accent-yellow/60 bg-surface-card shadow-[0_10px_40px_rgba(0,0,0,0.35)]">
            <div className="overflow-x-auto">
              <table className="w-[702px] sm:w-[916px] lg:w-full table-fixed border-separate border-spacing-0">
                {/* Explicit widths keep the frozen identity cells from growing
                    over FPPG when a player has a long name. At the mobile
                    breakpoint, rank + player + FPPG occupy 286px, leaving the
                    first secondary stat visible as a horizontal-scroll cue. */}
                <colgroup>
                  <col className="w-[40px] sm:w-[72px]" />
                  <col className="w-[188px] sm:w-[260px]" />
                  <col className="w-[58px] sm:w-[72px]" />
                  {statColumns.map(({ key }) => (
                    <col key={key as string} className="w-[52px] sm:w-[64px]" />
                  ))}
                </colgroup>
                {/* ===== Header ===== */}
                <thead>
                  <tr className="bg-accent-yellow/10 [&>th]:border-b [&>th]:border-accent-yellow/60">
                    <th
                      colSpan={2}
                      className="overflow-hidden text-left pl-2 sm:pl-6 pr-1 py-3 sm:py-4 font-heading font-bold text-sm sm:text-lg tracking-[1.5px] sm:tracking-[2px] uppercase text-white whitespace-nowrap sticky left-0 bg-surface-raised z-20 shadow-[6px_0_6px_-4px_rgba(0,0,0,0.55)]"
                    >
                      <span className="block truncate">
                        Top 10 {scopeLabel} by FPPG
                        {priorLabel && <span className="text-gray-400"> ({priorLabel})</span>}
                      </span>
                    </th>
                    <th className="font-heading font-bold text-caption sm:text-xs tracking-[2px] uppercase text-accent-yellow bg-accent-yellow/10 text-center px-2 sm:px-3 py-3 sm:py-4 whitespace-nowrap">
                      FPPG
                    </th>
                    {statColumns.map(({ label }) => (
                      <th
                        key={label}
                        className="font-heading font-bold text-caption sm:text-xs tracking-[2px] uppercase text-accent-yellow bg-primary-dark/60 text-center px-2 sm:px-3 py-3 sm:py-4 whitespace-nowrap"
                      >
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>

                {/* ===== Body ===== */}
                <tbody>
                  {isLoading && (
                    <tr>
                      <td colSpan={99} className="py-12 text-center">
                        <Spinner size="md" />
                      </td>
                    </tr>
                  )}
                  {!isLoading && error && (
                    <tr>
                      <td colSpan={99} className="py-8 text-center text-secondary italic text-sm">
                        {error}
                      </td>
                    </tr>
                  )}
                  {!isLoading && !error && players.length === 0 && (
                    <tr>
                      <td colSpan={99} className="py-8 text-center text-gray-500 italic text-sm">
                        No stats available yet.
                      </td>
                    </tr>
                  )}
                  {!isLoading &&
                    !error &&
                    players.map((p, idx) => {
                      const isEven = idx % 2 === 1;
                      const isLast = idx === players.length - 1;
                      const playerName = formatPlayerDisplayName({
                        id: p.id ?? p.player_id,
                        nickname: p.nickname ?? p.player_name,
                        name: p.name,
                      });
                      const position = p.primary_position || p.fantasy_position || p.fantasy_position_in_league;
                      const teamRaw = p.current_team_name;
                      const team = teamRaw ? getAbbreviation(teamRaw) || teamRaw : null;

                      return (
                        <tr
                          key={p.id || p.player_id || idx}
                          className={[isEven ? 'bg-primary-dark/30' : '', isLast ? '' : '[&>td]:border-b [&>td]:border-gray-700/40'].join(
                            ' '
                          )}
                        >
                          {/* Rank */}
                          <td className="overflow-hidden pl-2 pr-1 sm:pl-6 sm:pr-2 py-3 sm:py-4 text-left whitespace-nowrap sticky left-0 bg-surface-card z-10">
                            <RankBadge rank={idx + 1} />
                          </td>

                          {/* Player + pill */}
                          <td className="overflow-hidden py-3 sm:py-4 pr-2 text-left whitespace-nowrap sticky left-[40px] sm:left-[72px] bg-surface-card z-10 shadow-[6px_0_6px_-4px_rgba(0,0,0,0.55)]">
                            <span className="flex min-w-0 items-center gap-1.5 sm:gap-2">
                              <span className="min-w-0 truncate font-body font-bold text-[13px] sm:text-base text-white tracking-tight" title={playerName}>
                              {playerName}
                              </span>
                              <TeamPosPill team={team} position={position} />
                            </span>
                          </td>

                          {/* FPPG — highlighted column (the headline metric) */}
                          <td className="px-2 sm:px-3 py-3 sm:py-4 text-center whitespace-nowrap font-heading font-bold text-lg sm:text-xl tracking-wider text-accent-yellow bg-accent-yellow/5">
                            {p.fppg != null ? p.fppg.toFixed(1) : '—'}
                          </td>

                          {/* Stats */}
                          {statColumns.map(({ key }) => (
                            <StatCell key={key as string} value={Number(p[key]) || 0} />
                          ))}
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* CTAs */}
        <div className="flex items-center justify-center gap-3 flex-wrap">
          <button
            onClick={() => navigate(nextContestId ? `/dfs/build/${nextContestId}` : '/contests')}
            className="px-6 py-3 bg-accent-yellow text-primary font-heading font-bold text-sm uppercase tracking-wider rounded-lg hover:bg-accent-yellow/90 transition-colors"
          >
            Build a Lineup
          </button>
          <button
            onClick={() => navigate('/stats')}
            className="px-6 py-3 bg-transparent text-accent-yellow border-2 border-accent-yellow font-heading font-bold text-sm uppercase tracking-wider rounded-lg hover:bg-accent-yellow/10 transition-colors"
          >
            Research Players
          </button>
        </div>
      </div>
    </div>
  );
};

export default TopPlayersSection;
